import axios, { AxiosError, type AxiosInstance } from 'axios';

/**
 * Shared Axios client. Consumes the EXISTING backend contract only:
 * cookie auth (crm_token) via withCredentials; no token juggling on web.
 *
 * NOTE: every caller passes a full `/api/...` path, so the base is immutable
 * and empty. A configured or embedded base can double-prefix requests or move
 * sensitive CRM traffic off-origin.
 */
import { SAME_ORIGIN_API_BASE } from './sameOrigin';

export const api: AxiosInstance = axios.create({
  baseURL: SAME_ORIGIN_API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ── 401 handling ─────────────────────────────────────────────────────────────
// Registered by the app shell so we avoid an import cycle with the auth store.
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  unauthorizedHandler = fn;
}

// Requests where a 401 is an expected, handled-in-place outcome (don't trigger
// the global session-clear): the login attempt and the boot session probe.
function isAuthProbe(url?: string): boolean {
  if (!url) return false;
  return url.includes('/auth/login') || url.includes('/auth/me');
}

// ── Request interceptor ──────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    // FILE UPLOADS MUST NOT INHERIT THE JSON CONTENT TYPE.
    //
    // The instance sets `Content-Type: application/json` for every request,
    // which is right for the ~200 JSON calls and silently fatal for the few
    // multipart ones: axios only picks `multipart/form-data` — and only
    // generates the boundary the server needs to split the parts — when no
    // Content-Type is already set. With one set, a FormData body was serialised
    // as JSON instead, arriving as the literal `{"file":{}}`, and multer
    // answered "No file uploaded" for a file that had been chosen correctly.
    //
    // Deleting the header lets axios set it, boundary and all. Done here rather
    // than at each call site because every uploader in the app went through
    // this instance and every one of them was broken the same way.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // Hook point for correlation ids / per-request headers. Cookie is automatic.
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor ─────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string | { code?: string; message?: string } }>) => {
    const status = error.response?.status;
    if (status === 401 && !isAuthProbe(error.config?.url)) {
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  },
);

/** Normalize a backend error into a human message ({ error: '...' } shape). */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data?.error;
    if (typeof payload === 'string') return payload;
    if (payload && typeof payload.message === 'string') return payload.message;
    return error.message ?? fallback;
  }
  return fallback;
}

export function getApiErrorStatus(error: unknown): number | null {
  if (axios.isAxiosError(error)) return error.response?.status ?? null;
  return null;
}
